
import { Link } from 'react-router-dom';

interface Product {
  id: number;
  title: string;
  category: string;
  brand: string;
  model: string;
  certification: string;
  status: string;
  image: string;
  description: string;
}

interface ProductDetailHeroProps {
  product: Product;
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-red-600">
                <i className="ri-home-line w-4 h-4 mr-2"></i>
                Home
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <i className="ri-arrow-right-s-line w-4 h-4 text-gray-400"></i>
                <Link to="/products" className="ml-1 text-sm font-medium text-gray-700 hover:text-red-600 md:ml-2">
                  Products
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <i className="ri-arrow-right-s-line w-4 h-4 text-gray-400"></i>
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">{product.title}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden">
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            {/* Share Buttons */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Share:</span>
              <div className="flex space-x-2">
                <button className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <i className="ri-linkedin-fill text-sm"></i>
                </button>
                <button className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                  <i className="ri-whatsapp-fill text-sm"></i>
                </button>
                <button className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                  <i className="ri-mail-fill text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">{product.category}</p>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.title}</h1>
              <p className="text-lg text-gray-700 leading-relaxed">{product.description}</p>
            </div>

            {/* Product Info Grid */}
            <div className="grid grid-cols-2 gap-4 py-6 border-t border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Brand</p>
                <p className="font-semibold text-gray-900">{product.brand}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Model</p>
                <p className="font-semibold text-gray-900">{product.model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Certification</p>
                <p className="font-semibold text-red-600">{product.certification}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i className="ri-check-line w-3 h-3 mr-1"></i>
                  {product.status}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors text-center whitespace-nowrap"
                >
                  <i className="ri-mail-line mr-2"></i>
                  Request Quote
                </Link>
                <button className="flex-1 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap">
                  <i className="ri-download-line mr-2"></i>
                  Download Datasheet
                </button>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Need immediate assistance?</p>
                <a
                  href="https://wa.me/97455544093"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp Us: +974 5554 4093
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
